/**
 * Pre-Launch Access Hook
 *
 * Manages pre-launch phase gate:
 * - Fetches pre-launch status from backend
 * - Tracks countdown timer
 * - Handles access control logic
 * - Auto-refreshes after countdown ends
 */

import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuthContext } from '@/context/AuthContext';
import * as preLaunchApi from '@/api/preLaunch';

interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalSeconds: number;
}

export function usePreLaunchAccess() {
  const { isAuthenticated } = useAuthContext();
  const [countdown, setCountdown] = useState<CountdownTime | null>(null);

  // Fetch pre-launch status - only when authenticated
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['preLaunchStatus'],
    queryFn: async () => {
      const response = await preLaunchApi.getPreLaunchStatus();
      if (response.code !== 0) {
        throw new Error(response.message || 'Failed to fetch pre-launch status');
      }
      return response.data;
    },
    enabled: isAuthenticated, // Only fetch when user is authenticated
    staleTime: 5 * 60 * 1000, // 5 minutes - pre-launch status rarely changes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes to reduce API load
  });

  // Calculate countdown to fixed launch date
  const calculateCountdown = useCallback((): CountdownTime | null => {
    // Fixed launch date: October 30, 2025, 10:00 AM Singapore Time (UTC+8)
    const launchDate = '2025-10-30T10:00:00+08:00';

    const now = new Date().getTime();
    const end = new Date(launchDate).getTime();
    const diff = end - now;

    if (diff <= 0) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        totalSeconds: 0,
      };
    }

    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / (24 * 60 * 60));
    const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
    const seconds = totalSeconds % 60;

    return {
      days,
      hours,
      minutes,
      seconds,
      totalSeconds,
    };
  }, []);

  // Update countdown every second - always run to show countdown
  useEffect(() => {
    // Initial calculation
    const initialCountdown = calculateCountdown();
    setCountdown(initialCountdown);

    // If countdown has already ended, don't start interval
    if (initialCountdown?.totalSeconds === 0) {
      return;
    }

    // Update every second
    const interval = setInterval(() => {
      const newCountdown = calculateCountdown();
      setCountdown(newCountdown);

      // If countdown reaches zero, clear interval
      if (newCountdown?.totalSeconds === 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [calculateCountdown]);

  // Format countdown as string in hours format (e.g., "53:12:54")
  const formatCountdown = useCallback((time: CountdownTime | null): string => {
    if (!time) return '';

    const { days, hours, minutes, seconds } = time;

    // Convert days to hours and add to hours
    const totalHours = days * 24 + hours;

    return `${String(totalHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }, []);

  return {
    // Status
    isPreLaunch: data?.isPreLaunch ?? false,
    hasAccess: data?.hasAccess ?? true, // Default to true if not in pre-launch
    requiresInvite: data?.requiresInvite ?? false,
    isKol: data?.isKol ?? false,
    endTime: data?.endTime ?? null,

    // Countdown
    countdown,
    countdownString: formatCountdown(countdown),

    // Loading/Error
    isLoading,
    error,

    // Methods
    refetch,
  };
}
